import { createBrowserRouter, redirect } from 'react-router-dom';
import Root from '@customer/routes/root/root';
import ErrorPageRouter from '@cas/components/error-page-router/ErrorPageRouter';
import ProtectedRoute from '@customer/templates/protected-route/ProtectedRoute';
import AssetsList from '@customer/routes/assets/AssetsList';
import { Permission } from './permissions';
import SingleAsset from '@customer/routes/asset/SingleAsset';
import TechLibrary from '@customer/routes/techlibrary/TechLibrary';
import LandingPage from '@customer/routes/root/landing-page/LandingPage';
import Analytics from '@customer/routes/health/Analytics';
import Configuration from '@customer/routes/health/configuration/Configuration';
import Reporting from '@customer/routes/health/reporting/Reporting';
import SelfTest from '@customer/routes/health/self-test/SelfTest';
import MapPage from '@customer/routes/map/MapPage';
import SingleAssetSelfTest from '@customer/routes/asset/self-test/SingleAssetSelfTest';
import SingleAssetConfiguration from '@customer/routes/asset/configuration/SingleAssetConfiguration';
import SingleAssetDetails from '@customer/routes/asset/details/SingleAssetDetails';
import SingleAssetTerminal from '@customer/routes/asset/terminal/SingleAssetTerminal';
import NoAccess from '@customer/routes/root/no-access/NoAccess';
import Summary from '@customer/routes/health/summary/Summary';
import Home from '@customer/routes/home/Home';
import HealthPaywall from '@customer/routes/health/paywall/HealthPaywall';
import AssetsPaywall from '@customer/routes/assets/paywall/AssetsPaywall';
import HomePaywall from '@customer/routes/home/paywall/HomePaywall';
import SingleAssetInteractions from './routes/asset/interactions/SingleAssetInteractions';
import VehicleInteractions from './routes/performance/vehicle-interactions/VehicleInteractions';
import Performance from './routes/performance/Performance';
import SingleAssetVehicleInteractions from './routes/asset/interactions/vehicle-interactions/SingleAssetVehicleInteractions';
import SingleAssetGeofenceInteractions from './routes/asset/interactions/geofence-interactions/SingleAssetGeofenceInteractions';
import SoftwareStatus from './routes/health/configuration/software-status/SoftwareStatus';
import AssetNameMismatch from './routes/health/configuration/asset-name-mismatch/AssetNameMismatch';
import GeofenceInteractions from './routes/performance/geofence-interaction/GeofenceInteractions';
import { partnerDocs, techLibrary } from '@cas/components/icons/icons';
import PartnerResources from './routes/partners/partner-resources/PartnerResources';
import PerformancePaywall from './routes/performance/paywall/PerformancePaywall';
import Investigations from './routes/investigations/Investigations';
import Investigation from './routes/investigation/Investigation';
import SingleAssetActivity from './routes/asset/activity/SingleAssetActivity';
import SingleAssetInstallerConfig from './routes/asset/installer-config/SingleAssetInstallerConfig';

export interface NavLinkData {
  key: string;
  icon: string;
  to: string;
  titleKey: string;
  permission: Permission;
}

// Links in the side nav. Having this here makes it easier to keep in sync with routes!
export const navLinksPrimary: NavLinkData[] = [
  {
    key: 'Home',
    icon: 'home',
    to: '/home',
    titleKey: 'Home',
    permission: Permission.CanAccess,
  },
  {
    key: 'Assets',
    icon: 'asset',
    to: '/assets',
    titleKey: 'Assets',
    permission: Permission.CanAccess,
  },
  {
    key: 'CasHealth',
    icon: 'caution',
    to: '/health',
    titleKey: 'CasHealth',
    permission: Permission.CanAccess,
  },
  {
    key: 'CasPerformance',
    icon: 'dashboard',
    to: '/performance',
    titleKey: 'CasPerformance',
    permission: Permission.CanAccess,
  },
  {
    key: 'Investigations',
    icon: 'flag',
    to: '/investigations',
    titleKey: 'Investigations',
    permission: Permission.InvestigationsAll,
  }, // TODO revert to CanAccess once paywall video available
  {
    key: 'Map',
    icon: 'map',
    to: '/map',
    titleKey: 'Map',
    permission: Permission.FlagPreview,
  },
];

export const navLinksSecondary: NavLinkData[] = [
  {
    key: 'TechnicalLibrary',
    icon: techLibrary,
    to: '/techlibrary',
    titleKey: 'TechnicalLibrary',
    permission: Permission.CanAccess,
  },
  {
    key: 'PartnerResources',
    icon: partnerDocs,
    to: '/partners',
    titleKey: 'PartnerResources',
    permission: Permission.PartnerRead,
  },
];

// This catchall redirect must be used as the last child to catch any paths that haven't been matched.
const catchallRedirectToParent = { path: '*', loader: () => redirect('..') };

export const navRouter = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPageRouter />,
    children: [
      { index: true, element: <LandingPage /> },
      {
        path: 'home',
        element: (
          <ProtectedRoute permission={Permission.SiteDataRead} paywall={<HomePaywall />}>
            <Home />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'assets',
        element: (
          <ProtectedRoute permission={Permission.SiteDataRead} paywall={<AssetsPaywall />}>
            <AssetsList />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'asset/:assetId',
        element: (
          <ProtectedRoute permission={Permission.SiteDataRead} paywall={<AssetsPaywall />}>
            <SingleAsset />
          </ProtectedRoute>
        ),
        children: [
          { index: true, loader: () => redirect('self-test') },
          { path: 'self-test', element: <SingleAssetSelfTest /> },
          { path: 'configuration', element: <SingleAssetConfiguration /> },
          { path: 'details', element: <SingleAssetDetails /> },
          {
            path: 'interactions',
            element: (
              <ProtectedRoute permission={Permission.PerformanceRead}>
                <SingleAssetInteractions />
              </ProtectedRoute>
            ),
            children: [
              { index: true, loader: () => redirect('vehicle-interactions') },
              {
                path: 'vehicle-interactions',
                element: <SingleAssetVehicleInteractions />,
              },
              {
                path: 'geofence-interactions',
                element: <SingleAssetGeofenceInteractions />,
              },
              { ...catchallRedirectToParent },
            ],
          },
          { path: 'activity', element: <SingleAssetActivity /> },
          {
            path: 'terminal',
            element: (
              <ProtectedRoute permission={Permission.FlagPreview}>
                <SingleAssetTerminal />
              </ProtectedRoute>
            ),
          },
          {
            path: 'installer-config',
            element: (
              <ProtectedRoute permission={Permission.FlagPreview}>
                <SingleAssetInstallerConfig />
              </ProtectedRoute>
            ),
          },
          { ...catchallRedirectToParent },
        ],
      },
      {
        path: 'health',
        element: (
          <ProtectedRoute permission={Permission.SiteDataRead} paywall={<HealthPaywall />}>
            <Analytics />
          </ProtectedRoute>
        ),
        children: [
          { index: true, loader: () => redirect('summary') },
          { path: 'summary', element: <Summary /> },
          { path: 'self-test', element: <SelfTest /> },
          {
            path: 'configuration',
            element: <Configuration />,
            children: [
              { index: true, loader: () => redirect('software-status') },
              { path: 'software-status', element: <SoftwareStatus /> },
              { path: 'asset-name-mismatch', element: <AssetNameMismatch /> },
              { ...catchallRedirectToParent },
            ],
          },
          { path: 'reporting', element: <Reporting /> },
          { ...catchallRedirectToParent },
        ],
      },
      {
        path: 'performance',
        element: (
          <ProtectedRoute permission={Permission.PerformanceRead} paywall={<PerformancePaywall />}>
            <Performance />
          </ProtectedRoute>
        ),
        children: [
          { index: true, loader: () => redirect('vehicle-interactions') },
          { path: 'vehicle-interactions', element: <VehicleInteractions /> },
          { path: 'geofence-interactions', element: <GeofenceInteractions /> },
          { ...catchallRedirectToParent },
        ],
      },
      {
        path: 'investigations',
        element: (
          <ProtectedRoute permission={Permission.InvestigationsAll}>
            <Investigations />
          </ProtectedRoute>
        ), // TODO reinstate paywall once video available
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'investigation/:investigationId',
        element: (
          <ProtectedRoute permission={Permission.InvestigationsAll}>
            <Investigation />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'map',
        element: (
          <ProtectedRoute permission={Permission.FlagPreview}>
            <MapPage />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'techlibrary',
        element: (
          <ProtectedRoute>
            <TechLibrary />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },
      {
        path: 'partners',
        element: (
          <ProtectedRoute>
            <PartnerResources />
          </ProtectedRoute>
        ),
        children: [{ ...catchallRedirectToParent }],
      },

      // This 'no-access' route is used by the backend. Don't change this without a matching change in backend.
      { path: 'no-access', element: <NoAccess /> },

      // Redirect everything else to the root path.
      { ...catchallRedirectToParent },
    ],
  },
]);
